// "Meine Aufgaben": all cards of one owner across boards (GET /api/tasks?owner=…), grouped by
// board, with a status filter. A row opens its board focused on the card.
import { formatPoints, isoWeek, localDate, shortDate } from "./iso";
import { STATUS, STICKY, css } from "./palette";

export interface TaskRow {
  board: string; boardTitle: string; id: string; title: string; owner: string;
  /** People who help (C4); a task of the chosen person = owned or helped with. */
  assignees?: string[];
  status: number; color: number; due: string | null; week: string | null; mismatch: boolean;
  /** Story points (C8). */
  points?: number;
}
interface TaskList { owners: string[]; tasks: TaskRow[] }

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const ORDER = [1, 4, 2, 3];
const KEY = "lanewerk.tasks.status";

export class Tasks {
  private owner: string;
  private statuses: Set<number>;
  private data: TaskList = { owners: [], tasks: [] };

  /** @param me short name of the viewer (account or browser), the default owner */
  constructor(me: string) {
    document.title = "Meine Aufgaben – Lanewerk";
    const q = new URLSearchParams(location.search);
    this.owner = (q.get("owner") || me).toUpperCase();
    this.statuses = new Set(readStatuses());
    const chips = $("#lw-tasks-status");
    for (const s of ORDER) {
      const b = el("button", "chip", STATUS[s].label);
      b.type = "button";
      b.style.setProperty("--dot", css(STATUS[s].rgb));
      b.dataset.status = String(s);
      b.addEventListener("click", () => {
        if (this.statuses.has(s)) this.statuses.delete(s); else this.statuses.add(s);
        writeStatuses([...this.statuses]);
        this.render();
      });
      chips.appendChild(b);
    }
    $<HTMLSelectElement>("#lw-tasks-owner").addEventListener("change", (e) => {
      this.owner = (e.target as HTMLSelectElement).value;
      const p = new URLSearchParams(location.search);
      p.set("owner", this.owner);
      history.replaceState(null, "", `?${p}`);
      void this.load();
    });
    void this.load();
  }

  async load() {
    const status = $("#lw-tasks-error");
    try {
      const r = await fetch(`./api/tasks?owner=${encodeURIComponent(this.owner)}`, { headers: { accept: "application/json" } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      this.data = (await r.json()) as TaskList;
      status.textContent = "";
    } catch (e) {
      status.textContent = `Die Aufgaben konnten nicht geladen werden (${(e as Error).message}).`;
    }
    this.render();
  }

  private render() {
    const sel = $<HTMLSelectElement>("#lw-tasks-owner");
    const owners = [...new Set([...this.data.owners, this.owner].filter(Boolean))].sort((a, b) => a.localeCompare(b, "de"));
    sel.replaceChildren(...owners.map((o) => {
      const opt = el("option", undefined, o);
      opt.value = o;
      opt.selected = o === this.owner;
      return opt;
    }));
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-tasks-status .chip")) {
      b.setAttribute("aria-pressed", String(this.statuses.has(Number(b.dataset.status))));
    }
    const today = localDate(new Date());
    const now = isoWeek(new Date());
    const thisWeek = `${now.year}-W${String(now.week).padStart(2, "0")}`;
    const rows = this.data.tasks.filter((t) => this.statuses.size === 0 || this.statuses.has(t.status));
    const boards = new Map<string, TaskRow[]>();
    for (const t of rows) {
      const list = boards.get(t.board) ?? [];
      list.push(t);
      boards.set(t.board, list);
    }
    const box = $("#lw-tasks-list");
    box.replaceChildren(...[...boards.values()].map((list) => {
      const sec = el("section", "task-board");
      const head = el("div", "task-board-head");
      const h = el("h2", undefined, list[0].boardTitle || list[0].board);
      const open = el("a", "task-board-open", "Board öffnen");
      open.href = `?board=${encodeURIComponent(list[0].board)}`;
      head.append(h, open);
      sec.appendChild(head);
      const ul = el("ul", "task-rows");
      for (const t of list) ul.appendChild(this.row(t, today, thisWeek));
      sec.appendChild(ul);
      return sec;
    }));
    const n = rows.length;
    $("#lw-tasks-count").textContent = `${n} ${n === 1 ? "Aufgabe" : "Aufgaben"}${boards.size ? ` auf ${boards.size} ${boards.size === 1 ? "Board" : "Boards"}` : ""}`;
    const empty = $("#lw-tasks-empty");
    empty.hidden = n > 0;
    empty.textContent = this.data.tasks.length
      ? "Keine Aufgabe passt zum Status-Filter."
      : `Keine Karten mit dem Kürzel ${this.owner}. Das Kürzel setzt du auf einer Karte mit Rechtsklick → Verantwortlich oder in den Details (Taste I) unter Mitwirkende.`;
  }

  private row(t: TaskRow, today: string, thisWeek: string): HTMLLIElement {
    const li = el("li");
    const a = el("a", "task");
    a.href = `?board=${encodeURIComponent(t.board)}&card=${encodeURIComponent(t.id)}`;
    const swatch = el("span", "task-swatch");
    swatch.style.background = css((STICKY[t.color] ?? STICKY[0]).rgb);
    const dot = el("span", "dot");
    dot.style.background = css((STATUS[t.status] ?? STATUS[1]).rgb);
    dot.title = (STATUS[t.status] ?? STATUS[1]).label;
    const title = el("span", "task-title", t.title || "Ohne Titel");
    if (!t.title) title.classList.add("muted");
    // helping on someone else's card: say whose it is
    if (t.owner.toUpperCase() !== this.owner) {
      const role = el("span", "task-role", t.owner ? `mitwirkend · ${t.owner}` : "mitwirkend");
      title.appendChild(role);
    }
    if (t.points != null) {
      const sp = el("span", "task-points", formatPoints(t.points));
      sp.title = "Story Points";
      title.appendChild(sp);
    }
    const week = el("span", "task-week", t.week ? `KW ${Number(t.week.split("-W")[1])}` : "–");
    if (t.week === thisWeek) week.classList.add("now");
    a.append(swatch, dot, title, week);
    if (t.due) {
      const overdue = t.due < today && (t.status === 1 || t.status === 4);
      const due = el("span", "task-due", `Fällig ${shortDate(t.due)}`);
      if (overdue || t.mismatch) due.classList.add("bad");
      due.title = overdue ? "Überfällig" : t.mismatch ? "Fälligkeit liegt nicht in der Kalenderwoche der Karte" : "";
      a.appendChild(due);
    }
    li.appendChild(a);
    return li;
  }
}

function readStatuses(): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (Array.isArray(v)) return v.filter((x) => ORDER.includes(x));
  } catch { /* storage blocked */ }
  return [1, 4]; // open and waiting
}
function writeStatuses(v: number[]) {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* not kept */ }
}
